import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { ClipboardList, Users, DollarSign, Inbox, ArrowUpRight, LogIn } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [logins, setLogins] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: o }, { data: l }] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/recent-logins", { params: { limit: 8 } }).catch(() => ({ data: [] })),
      ]);
      setStats(s);
      setRecent(o.slice(0, 6));
      setLogins(l || []);
    })();
  }, []);

  if (!stats) return <AdminLayout><div className="text-sm text-inkSoft">Loading…</div></AdminLayout>;

  const cards = [
    { icon: ClipboardList, label: "Total orders", value: stats.total_orders },
    { icon: Inbox, label: "Pending fulfillment", value: (stats.by_status?.pending || 0) + (stats.by_status?.in_review || 0) + (stats.by_status?.in_progress || 0) },
    { icon: Users, label: "Customers", value: stats.total_users },
    { icon: DollarSign, label: "Booked revenue", value: `$${(stats.revenue || 0).toLocaleString()}` },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="eyebrow mb-2">Admin overview</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Fulfillment desk</h1>
        <p className="text-inkSoft mt-1">Operational view across the entire Zenith Ranking marketplace.</p>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-soft p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-surfaceAlt grid place-items-center text-terra"><Icon size={16} /></div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-inkSoft">{c.label}</div>
                  <div className="font-mono text-2xl font-semibold tracking-tighter">{c.value}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 card-soft overflow-hidden" data-testid="admin-recent-orders">
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold tracking-tight">Recent orders</h2>
          <Link to="/admin/orders" className="text-sm text-terra hover:underline inline-flex items-center gap-1">View all <ArrowUpRight size={14} /></Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-6 text-sm text-inkSoft">No orders yet.</div>
        ) : (
          <ul>
            {recent.map((o) => (
              <li key={o.order_id} className="px-6 py-4 border-b border-line last:border-0 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.package_name || o.service_slug}</div>
                  <div className="text-xs text-inkSoft font-mono">{o.customer_name || "—"} · {o.customer_email}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs font-mono text-inkSoft">{o.status.replace("_", " ")}</span>
                  <Link to={`/admin/orders/${o.order_id}`} className="text-sm text-ink hover:text-terra">Open</Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8 card-soft overflow-hidden" data-testid="admin-recent-logins">
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogIn size={16} className="text-terra" />
            <h2 className="font-display text-xl font-semibold tracking-tight">Recent logins</h2>
          </div>
          <button
            onClick={async () => {
              try {
                await api.post("/admin/send-weekly-digest-now");
                alert("Weekly digest sent to notification inbox.");
              } catch (e) {
                alert("Could not send digest: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="text-xs text-terra hover:underline"
            data-testid="admin-send-digest-now"
          >
            Send digest now →
          </button>
        </div>
        {logins.length === 0 ? (
          <div className="p-6 text-sm text-inkSoft">No login activity recorded yet.</div>
        ) : (
          <ul>
            {logins.map((e) => (
              <li key={e.event_id} className="px-6 py-3 border-b border-line last:border-0 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">{e.name || "Unknown"} <span className="text-inkSoft text-xs font-mono">· {e.email}</span></div>
                  <div className="text-[11px] text-inkSoft font-mono truncate">{e.method} · {e.ip || "no ip"} · {(e.user_agent || "").slice(0, 60)}</div>
                </div>
                <div className="text-xs text-inkSoft font-mono shrink-0">{new Date(e.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
